// Customer-facing artwork upload widget — the RESIDUAL block.
//
// As of the inline-upload change, the cover + guts uploads (and the hardcover
// endsheet / embossing uploads) render INLINE inside their configurator
// sections (SchemaForm.inlineUploadSlots). This component holds what's left for
// a line: the booklet "Hands-on Prep" (combined) slot — shown ONLY while the
// customer hasn't supplied their own cover/guts above — and the dedicated
// finishing-driven slots (emboss / foil / personalization data) passed in as
// `extraSlots`. The `insert` slot is dormant (an insert is its own project) and
// no longer rendered. Flat work (SingleSheet / Labels) still renders a single
// flat drop zone.
//
// Asset state is OWNED by the parent (useLineAssets) and passed in as
// `lineAssets`, so this block and the inline SchemaForm slots share ONE source
// of truth — an upload/delete in either surface shows in both immediately.
//
// Layout modes:
//   - Flat (default / SingleSheet / Labels): single drop zone, role 'general'.
//   - Role-grouped (booklets, MediaDuplication): one section per remaining
//     component slot from FAMILY_COMPONENT_ROLES, minus the roles now rendered
//     inline (cover/guts) and the roles that became their own surfaces
//     (dust_jacket → separate project, endsheet → hardcover inline slot).
//     Booklets keep the "Send as one file" combined slot for the hands-on
//     prep path (matches the either-or gate in shared/validateRoleSet).

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  COMPONENT_ROLE_HELP,
  FAMILY_COMPONENT_ROLES,
  familyRequiredRoles,
  MAX_UPLOAD_MB,
  type AssetDto,
  type ComponentRole,
  type ComponentRoleSpec,
} from 'shared';
import type { ProductType } from 'shared';

import { formatNumber } from '../i18n/formatters.js';
import type { UploadSlotSpec } from './SchemaForm.js';
import {
  ArtworkBarcodeNotice,
  AssetGrid,
  Dropzone,
  UploadSlot,
  type ArtworkPendingToggleFor,
} from './UploadSlot.js';
import type { LineAssets, PendingUpload } from './useLineAssets.js';

// Product types where the uploader splits into role-scoped slots
// instead of rendering a single flat drop zone. Flat-print types
// (single_sheet, labels) stay flat.
const ROLE_GROUPED_FAMILIES: ReadonlySet<string> = new Set([
  'saddle_stitch_book',
  'perfect_bound_book',
  'hard_bound_book',
  'coil_or_wire_o_book',
  'loose_leaf',
  'media_replication',
  // Custom packaging: its one role, `custom_packaging_art`, is served by the gated
  // inline slot in Packaging details (RESIDUAL_HIDDEN_ROLES drops the standing
  // one), so stock or unprinted packaging shows no upload block rather than the
  // flat `general` catch-all.
  'custom_packaging',
  // An assembly ITEM, with an EMPTY role set (FAMILY_COMPONENT_ROLES.assembly),
  // which is the point rather than an oversight. Listing it here selects the
  // role-grouped layout, and a role-grouped layout with no roles renders no
  // dropzone — so the item's only upload affordance is the dedicated `kit_art`
  // slot the schema reveals, instead of the untargeted flat catch-all that a
  // non-listed family falls through to (that catch-all writes role 'general',
  // which would also drag in the barcode advisory and the size/ink pre-fill
  // scan — neither of which means anything on a kit diagram).
  //
  // Listing it ALONE would be the mistake the tracker warns about: with no
  // dedicated slot beside it the block collapses to a bare heading. It does not
  // collapse here, because `kit_art_file_reference` is gated only on the product
  // type, so an assembly item always has exactly one slot. Should that ever stop
  // being true, roleGroupedEmpty suppresses the whole block rather than showing
  // an empty one. Files stranded under another role (a `general` upload made
  // before the customer switched this line to Assembly) still surface in the
  // "_other" bucket, so they stay removable.
  'assembly',
]);

// Roles that no longer render as standing slots in this residual block:
//   dust_jacket → captured as its OWN project (a separate single-sheet line).
//   endsheet    → captured as a hardcover-gated inline slot in the Cover section.
//   insert      → captured as its OWN single_sheet project (a preprinted page or
//                 card is a separate piece, not artwork bound into THIS line).
// All three roles are KEPT in FAMILY_COMPONENT_ROLES / ALL_COMPONENT_ROLES so old
// pinned quotes + historical imports that reference them still resolve, and any
// stranded file surfaces in the "_other" bucket below — disuse, not deletion.
const RESIDUAL_HIDDEN_ROLES: ReadonlySet<ComponentRole> = new Set<ComponentRole>([
  'dust_jacket',
  'endsheet',
  'insert',
  // disc_art → captured inline in the Media details section, gated on
  // `media_decoration`. A standing slot here as well would put two uploaders on
  // one role, and the extraSlots de-dup below resolves that by dropping the GATED
  // one — leaving an always-on slot on a data-only media run that has no
  // art to give it.
  'disc_art',
  // custom_packaging_art → captured inline in Packaging details while it prints, for the same
  // reason as disc_art.
  'custom_packaging_art',
]);

// The "finished components" a product is made of when the customer supplies
// their own print-ready files: the roles the family actually DEMANDS a file for,
// under the SAME escape hatches the price and submit gates apply (a self-cover
// booklet needs no cover; a collapsed cover+inside pair needs one `combined`
// file and neither component). Their presence means the customer has chosen the
// separate-files path — which is what the `combined` "Hands-on Prep" slot is the
// ALTERNATIVE to (one pile of files, our prepress team splits + preps by hand).
// Once any of them is uploaded — inline above, in a role-grouped slot, or in the
// flat zone — Hands-on Prep is hidden so we're not offering to combine files the
// customer has already separated for us.
//
// Read from the shared `familyRequiredRoles` rather than a fixed cover/guts pair.
// The fixed pair matched only the booklets, so flat work (whose finished
// component is `general`) kept offering Hands-on Prep underneath artwork it had
// already been given — the defect this replaces. A family whose roles are ALL
// optional (media_replication: disc art, packaging, insert, any subset of which
// may apply) yields an empty set and keeps its catch-all, which is right: it can
// still have more to hand over after supplying one of them.
function finalRolesFor(
  family: ProductType | string | undefined,
  escapeHatches: { self_cover: boolean; combined_artwork: boolean },
): ReadonlySet<ComponentRole> {
  return family ? familyRequiredRoles(family, escapeHatches) : new Set<ComponentRole>();
}

type Props = {
  // Per-line asset state + handlers (owned by the parent LineEditor via
  // useLineAssets, shared with the inline SchemaForm slots).
  lineAssets: LineAssets;
  // Top-level product family from the configurator's product_type picker.
  // When in ROLE_GROUPED_FAMILIES the uploader renders per-role slots;
  // otherwise a flat drop zone.
  family?: ProductType | string;
  // Optional override for the section heading (e.g. "Artwork for <item>").
  heading?: string;
  // Disable the input (quote locked / paid / cancelled). Defaults to enabled.
  disabled?: boolean;
  // Roles already rendered INLINE in the configurator sections (cover/guts +
  // hardcover endsheet/embossing) — de-duped out of the role-grouped layout
  // here so they never double up. From SchemaForm.inlineUploadRoles.
  inlineRoles?: ReadonlySet<ComponentRole>;
  // Dedicated, conditionally-revealed slots from the schema's currently-visible
  // file_reference fields that are NOT inline (emboss / foil / personalization
  // data) — see SchemaForm.deriveUploadSlots. Rendered in an appended section.
  // Slots whose role is already covered by the family's role-grouped layout are
  // de-duped here. Absent/empty → no appended section.
  extraSlots?: UploadSlotSpec[];
  // The two config escape hatches that retire a REQUIRED component for this
  // line: `self_cover` (the outer guts sheet is the cover — no cover file) and
  // `combined_artwork` (cover + inside pages in ONE file — no separate cover or
  // interior file). Both hide their slots via the schema's conditional_show, so
  // neither role reaches `inlineRoles` and this block would otherwise keep
  // rendering a standing, asterisked slot for a file the customer was told not
  // to supply. Read the same rule the price and submit gates read (R12/R13) by
  // handing them to familyRequiredRoles. From the line's own config.
  selfCover?: boolean;
  combinedArtwork?: boolean;
  // The on-screen label of the `combined_artwork` toggle while the form offers
  // it (an un-ticked cover-bearing book), else null. When set, the Hands-on Prep
  // slot leads with a pointer to that toggle: a cover + inside pages PDF is
  // print-ready and belongs there, where it skips the prep fee
  // (lineNeedsHandsOnPrep), not in this catch-all, where it is charged.
  combinedArtworkToggleLabel?: string | null;
  // Quote-level fulfillment signal (shared `orderRequiresBarcode`) → the barcode
  // advisory under each slot reads "required for fulfillment" instead of
  // "optional". Threaded into every UploadSlot + the flat layout below.
  barcodeRequired?: boolean;
  // "Quote now, art to follow", per slot: the line's lookup from a slot's role to
  // its mark, or null where the line cannot mark that slot (an optional slot, a
  // dedicated emboss/foil/data slot). Omit it to offer no checkbox anywhere.
  artworkPendingToggle?: ArtworkPendingToggleFor;
};

export function ArtworkUploader({
  lineAssets,
  family,
  heading,
  disabled = false,
  inlineRoles,
  extraSlots,
  selfCover = false,
  combinedArtwork = false,
  combinedArtworkToggleLabel = null,
  barcodeRequired = false,
  artworkPendingToggle,
}: Props): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const {
    assets,
    pending,
    loadingList,
    bannerError,
    bannerTone,
    handleFiles,
    handleDelete,
    dismissPending,
    reusable,
    attachReusable,
  } = lineAssets;

  // Flat work (single_sheet / labels) now renders its 'general' artwork upload
  // INLINE at the top of the Size section (SchemaForm), the same way books lead
  // with cover/guts. When it does, this residual block must NOT also render its
  // own flat dropzone for 'general' — that would double the upload. The inline
  // roles tell us; everything else (emboss/foil/variable-data) still lives here.
  const generalInline = inlineRoles?.has('general') ?? false;

  // Has the customer already uploaded a finished component (cover / interior)
  // above? If so they're on the separate-files path and the `combined`
  // "Hands-on Prep" slot — the offer to split ONE file into cover + guts for
  // them — no longer applies, so it's dropped from the layout below. Pending
  // counts so the slot disappears the moment a drop starts, not after it lands.
  const finalRoles = useMemo(
    () => finalRolesFor(family, { self_cover: selfCover, combined_artwork: combinedArtwork }),
    [family, selfCover, combinedArtwork],
  );
  const hasFinalsUploaded = useMemo(
    () =>
      assets.some((a) => finalRoles.has(a.component_role)) ||
      pending.some((p) => finalRoles.has(p.component_role)),
    [assets, pending, finalRoles],
  );

  // Decide layout mode + which role specs to render slots for. Drop the roles
  // now rendered inline (cover/guts) and the retired standing roles
  // (dust_jacket/endsheet/insert) from the family's role-grouped layout, drop a
  // required component the line's config has retired (`self_cover` /
  // `combined_artwork` — see finalRoles), and drop `combined` once the customer
  // has supplied their own finished files. Without the config check a collapsed
  // cover+inside pair rendered its one inline "Cover + inside pages" slot AND a
  // standing "Cover *" + "Guts *" pair down here: three dropzones asking for a
  // file the customer was told to send once.
  const roleSpecs: readonly ComponentRoleSpec[] | null = useMemo(() => {
    if (!family || !ROLE_GROUPED_FAMILIES.has(family)) return null;
    const specs = FAMILY_COMPONENT_ROLES[family as ProductType] ?? null;
    if (!specs) return null;
    return specs.filter(
      (s) =>
        !RESIDUAL_HIDDEN_ROLES.has(s.role) &&
        !(inlineRoles?.has(s.role) ?? false) &&
        !(s.required && !finalRoles.has(s.role)) &&
        !(s.role === 'combined' && hasFinalsUploaded),
    );
  }, [family, inlineRoles, finalRoles, hasFinalsUploaded]);

  // Flat families (single_sheet / labels) render their required 'general'
  // artwork inline, then offer the SAME "just give us all the files" catch-all
  // the booklets have — so a partner whose only file is a Word doc, an InDesign
  // package, a spreadsheet, or any format the artwork slot won't take has a home
  // for it (prepress preps/converts by hand).
  //
  // Files already dropped on the flat catch-all, bucketed for its own slot.
  const combinedAssets = useMemo(
    () => assets.filter((a) => a.component_role === 'combined'),
    [assets],
  );
  const combinedPending = useMemo(
    () => pending.filter((p) => p.component_role === 'combined'),
    [pending],
  );

  // Gated on the family actually carrying a `combined` spec so it never appears
  // where it shouldn't — and dropped once the customer has uploaded the family's
  // finished component (for flat work, the inline `general` artwork), the same
  // hasFinalsUploaded gate the role-grouped layout applies to its `combined`
  // spec. Without it a label or single-sheet line kept asking for "all the
  // files" underneath artwork it had already been given. A pile already dropped
  // here keeps the slot on screen so it stays visible and removable.
  const flatCatchAll =
    roleSpecs == null &&
    !!family &&
    (FAMILY_COMPONENT_ROLES[family as ProductType]?.some((s) => s.role === 'combined') ?? false) &&
    (!hasFinalsUploaded || combinedAssets.length > 0 || combinedPending.length > 0);

  // Dedicated, conditionally-revealed slots (emboss / foil / variable_data),
  // de-duped against the family's role-grouped slots AND the inline roles so a
  // role rendered elsewhere doesn't appear twice.
  const familyRoles = useMemo(
    () => new Set<ComponentRole>((roleSpecs ?? []).map((s) => s.role)),
    [roleSpecs],
  );
  const appendedSlots = useMemo(
    () =>
      (extraSlots ?? []).filter(
        (s) => !familyRoles.has(s.role) && !(inlineRoles?.has(s.role) ?? false)
      ),
    [extraSlots, familyRoles, inlineRoles],
  );
  const appendedRoles = useMemo(
    () => new Set<ComponentRole>(appendedSlots.map((s) => s.role)),
    [appendedSlots],
  );

  // Roles rendered SOMEWHERE OTHER than this residual block's role-grouped /
  // flat layout (inline in SchemaForm, or in the appended dedicated section).
  // Their assets must not fall into the synthetic "_other" bucket or the flat
  // grid — they show in their own slot.
  const elsewhereRoles = useMemo(() => {
    const s = new Set<ComponentRole>(appendedRoles);
    if (inlineRoles) for (const r of inlineRoles) s.add(r);
    // The flat catch-all owns 'combined' files — keep them out of the flat grid.
    if (flatCatchAll) s.add('combined');
    return s;
  }, [appendedRoles, inlineRoles, flatCatchAll]);

  // Bucket assets + pending uploads by role for the grouped layout.
  const grouped = useMemo(() => {
    if (!roleSpecs) return null;
    const buckets: Record<string, { assets: AssetDto[]; pending: PendingUpload[] }> = {};
    for (const spec of roleSpecs) {
      buckets[spec.role] = { assets: [], pending: [] };
    }
    for (const a of assets) {
      const r = a.component_role;
      if (buckets[r]) buckets[r].assets.push(a);
      // A role rendered elsewhere (inline / appended) owns its own files —
      // don't also dump them into the synthetic "other" bucket.
      else if (elsewhereRoles.has(r as ComponentRole)) continue;
      else {
        // Asset uploaded under a role no longer in this layout (e.g. customer
        // switched family, or a now-retired/relocated role like a stranded
        // cover after toggling self_cover) — surface it under a synthetic
        // "other" bucket so they can see and remove it.
        buckets['_other'] ??= { assets: [], pending: [] };
        buckets['_other'].assets.push(a);
      }
    }
    for (const p of pending) {
      const r = p.component_role;
      if (buckets[r]) buckets[r].pending.push(p);
    }
    return buckets;
  }, [roleSpecs, assets, pending, elsewhereRoles]);

  // Bucket assets + pending for the appended dedicated slots, by role.
  const appendedBuckets = useMemo(() => {
    const m: Record<string, { assets: AssetDto[]; pending: PendingUpload[] }> = {};
    for (const s of appendedSlots) m[s.role] = { assets: [], pending: [] };
    for (const a of assets) {
      const b = m[a.component_role];
      if (b) b.assets.push(a);
    }
    for (const p of pending) {
      const b = m[p.component_role];
      if (b) b.pending.push(p);
    }
    return m;
  }, [appendedSlots, assets, pending]);

  // In the flat layout, hide files that belong to a role rendered elsewhere
  // (inline / appended) — they render in their own slot, not the catch-all grid.
  const flatAssets = useMemo(
    () => assets.filter((a) => !elsewhereRoles.has(a.component_role)),
    [assets, elsewhereRoles],
  );
  const flatPending = useMemo(
    () => pending.filter((p) => !elsewhereRoles.has(p.component_role)),
    [pending, elsewhereRoles],
  );

  // A role-grouped family can end up with NOTHING to collect here once the
  // customer has supplied their finished files inline above (cover/guts) and
  // the dormant insert / Hands-on Prep slots are hidden. Rather than leave a
  // bare "Artwork for <item>" heading over an empty container, suppress the
  // whole block. Still render when there are stranded "_other" files to clear,
  // dedicated (emboss/foil/variable-data) slots to fill, or an error to show.
  const roleGroupedEmpty =
    roleSpecs != null &&
    roleSpecs.length === 0 &&
    !(grouped?.['_other']?.assets.length ?? 0) &&
    appendedSlots.length === 0 &&
    !bannerError;
  // Flat work whose 'general' upload moved inline: the residual block holds only
  // the appended dedicated slots (emboss/foil/variable-data) plus any stranded
  // files. With none of those and no error, render nothing — the same
  // self-suppression a role-grouped family gets once its components are inline.
  const flatInlineEmpty =
    roleSpecs == null &&
    generalInline &&
    appendedSlots.length === 0 &&
    flatAssets.length === 0 &&
    flatPending.length === 0 &&
    // The catch-all is an always-available drop area — keep the block visible
    // whenever a flat family offers it, even with nothing uploaded yet.
    !flatCatchAll &&
    !bannerError;
  if (roleGroupedEmpty || flatInlineEmpty) return <></>;

  return (
    <div className="artwork-uploader">
      <div className="artwork-uploader__header">
        <h3 style={{ margin: 0 }}>{heading ?? t('artwork.header.title')}</h3>
        <span className="help" style={{ marginLeft: 8 }}>
          {t('artwork.header.sizeLimitNote', { size: formatNumber(MAX_UPLOAD_MB) })}
        </span>
      </div>

      {bannerError && (
        <div
          className={`banner ${bannerTone === 'info' ? 'banner--info' : 'banner--error'}`}
          style={{ marginBottom: 12 }}
        >
          {bannerError}
        </div>
      )}

      {roleSpecs && grouped ? (
        <div className="artwork-uploader__slots">
          {roleSpecs.map((spec) => (
            <UploadSlot
              key={spec.role}
              spec={spec}
              help={
                spec.role === 'combined' && combinedArtworkToggleLabel
                  ? `${t('artwork.combinedPairNote', { toggle: combinedArtworkToggleLabel })} ${COMPONENT_ROLE_HELP.combined}`
                  : undefined
              }
              assets={grouped[spec.role]?.assets ?? []}
              pending={grouped[spec.role]?.pending ?? []}
              disabled={disabled}
              onPickFiles={(files) => handleFiles(files, spec.role)}
              onDelete={handleDelete}
              onDismissPending={dismissPending}
              barcodeRequired={barcodeRequired}
              artworkPending={artworkPendingToggle?.(spec.role)?.pending}
              onArtworkPendingChange={artworkPendingToggle?.(spec.role)?.onChange}
              reusable={reusable}
              onReuse={attachReusable}
            />
          ))}
          {grouped['_other'] && grouped['_other'].assets.length > 0 && (
            <UploadSlot
              spec={{ role: 'general', required: false }}
              label={t('artwork.otherFiles.label')}
              help={t('artwork.otherFiles.help')}
              assets={grouped['_other'].assets}
              pending={[]}
              disabled={disabled}
              onPickFiles={() => undefined}
              onDelete={handleDelete}
              onDismissPending={dismissPending}
              hideDropzone
            />
          )}
        </div>
      ) : (
        <FlatLayout
          assets={flatAssets}
          pending={flatPending}
          loadingList={loadingList}
          disabled={disabled}
          onPickFiles={(files) => handleFiles(files, 'general')}
          onDelete={handleDelete}
          onDismissPending={dismissPending}
          // 'general' now uploads via the inline Size-section slot, so suppress
          // this block's flat dropzone. Any stranded files still show below it
          // (e.g. a cover uploaded before switching the line to a flat family),
          // so they stay removable.
          hideDropzone={generalInline}
          barcodeRequired={barcodeRequired}
        />
      )}

      {/* Flat-family "just give us all the files" catch-all — same Hands-on
          Prep slot the booklets carry, so flat work also has a home for any
          format (Word, InDesign, spreadsheets, fonts) that the artwork slot
          won't take. Accepts anything; prepress preps/converts by hand. */}
      {flatCatchAll && (
        <div className="artwork-uploader__slots">
          <UploadSlot
            spec={{ role: 'combined', required: false }}
            assets={combinedAssets}
            pending={combinedPending}
            disabled={disabled}
            onPickFiles={(files) => handleFiles(files, 'combined')}
            onDelete={handleDelete}
            onDismissPending={dismissPending}
            barcodeRequired={barcodeRequired}
            artworkPending={artworkPendingToggle?.('combined')?.pending}
            onArtworkPendingChange={artworkPendingToggle?.('combined')?.onChange}
            reusable={reusable}
            onReuse={attachReusable}
          />
        </div>
      )}

      {/* Dedicated, conditionally-revealed slots (emboss / foil artwork,
          personalization data) — appended below whatever layout the family
          uses. Each is scoped to its component_role; uploads here tag the
          Asset with that role. Reveal/hide is driven by the schema's
          file_reference conditional_show (see SchemaForm.deriveUploadSlots). */}
      {appendedSlots.length > 0 && (
        <div className="artwork-uploader__slots artwork-uploader__dedicated">
          <p className="help artwork-uploader__dedicated-intro">
            {t('artwork.dedicated.intro', { count: appendedSlots.length })}
          </p>
          {appendedSlots.map((s) => (
            <UploadSlot
              key={s.role}
              spec={{ role: s.role, required: s.required }}
              label={s.label}
              help={s.help ?? undefined}
              assets={appendedBuckets[s.role]?.assets ?? []}
              pending={appendedBuckets[s.role]?.pending ?? []}
              disabled={disabled}
              onPickFiles={(files) => handleFiles(files, s.role)}
              onDelete={handleDelete}
              onDismissPending={dismissPending}
              barcodeRequired={barcodeRequired}
              artworkPending={artworkPendingToggle?.(s.role)?.pending}
              onArtworkPendingChange={artworkPendingToggle?.(s.role)?.onChange}
              reusable={reusable}
              onReuse={attachReusable}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Flat layout (SingleSheet / Labels / draft) ───────────────────────
function FlatLayout({
  assets,
  pending,
  loadingList,
  disabled,
  onPickFiles,
  onDelete,
  onDismissPending,
  hideDropzone = false,
  barcodeRequired = false,
}: {
  assets: AssetDto[];
  pending: PendingUpload[];
  loadingList: boolean;
  disabled: boolean;
  onPickFiles: (files: ArrayLike<File> | null) => void;
  onDelete: (a: AssetDto) => void;
  onDismissPending: (local_id: string) => void;
  // When the flat 'general' upload renders inline in the Size section, the
  // dropzone (and the empty "no files yet" prompt + initial loading shimmer) is
  // suppressed here — only any stranded files' thumbnails remain, so they stay
  // removable. The inline slot is where the customer actually uploads.
  hideDropzone?: boolean;
  // Quote-level fulfillment signal → "required" vs "optional" barcode copy.
  barcodeRequired?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  return (
    <>
      {!hideDropzone && <Dropzone disabled={disabled} onPickFiles={onPickFiles} multiple />}

      {(pending.length > 0 || assets.length > 0 || (loadingList && !hideDropzone)) && (
        <AssetGrid
          assets={assets}
          pending={pending}
          disabled={disabled}
          onDelete={onDelete}
          onDismissPending={onDismissPending}
          loadingPlaceholder={loadingList && pending.length === 0 && assets.length === 0}
        />
      )}

      {!hideDropzone && !loadingList && assets.length === 0 && pending.length === 0 && (
        <p className="help" style={{ marginTop: 12 }}>
          {t('artwork.flat.emptyState')}
        </p>
      )}

      {/* Barcode advisory for the flat path (single sheet / labels), scoped to
          the files dropped here — same per-slot placement the role-grouped
          UploadSlot uses, so the notice sits right under this artwork. Suppressed
          when the dropzone is hidden (the 'general' upload moved inline): the
          inline UploadSlot owns the advisory for those files, and the only assets
          left here are stranded ones being surfaced for cleanup, not artwork the
          customer is actively scanning. */}
      {!hideDropzone && <ArtworkBarcodeNotice assets={assets} barcodeRequired={barcodeRequired} />}
    </>
  );
}
